import React from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import CopyButton from 'common/components/CopyButton';
import { Text } from 'common/styledComponents/typography';
import { useShowToast } from 'common/hooks/notification';
import { useDataExportWebhookDeliveries, useRedeliverDataExportDelivery } from 'common/queries/dataExport';
import { DataExportDelivery, DataExportDeliveryStatus, DataExportWebhook } from 'common/types/dataExport';
import { SectionError, SectionLoading } from 'modules/settings/components/DataExport/SectionStatus';
import { CodeBlock } from 'modules/settings/components/DataExport/GuideContent';
import { errorToast, formatExportDateTime } from 'modules/settings/components/DataExport/utils';
import {
  BadgeTone,
  Code,
  DeliveriesPanel,
  DeliveriesScroller,
  DeliveriesTable,
  ToneBadge,
} from 'modules/settings/components/DataExport/styled';

const STATUS_TONES: Record<DataExportDeliveryStatus, BadgeTone> = { pending: 'gray', delivered: 'green', failed: 'red' };
const COLUMNS = ['created', 'event', 'deliveryId', 'status', 'attempts', 'lastAnswer', 'outcome', 'payload'];

/** The body as sent is compact JSON: shown formatted, and copied exactly as sent. */
const formatPayload = (payload: string): string => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

interface Props {
  webhook: DataExportWebhook;
  retentionDays?: number;
}

/** The expanded "Recent deliveries" of one webhook: what was sent, how often, what the endpoint answered. */
const WebhookDeliveries: React.FC<Props> = ({ webhook, retentionDays }) => {
  const { t } = useTranslation();
  const showToast = useShowToast();
  const { data, isLoading, isError, error, refetch } = useDataExportWebhookDeliveries(webhook.id, true);
  const redeliver = useRedeliverDataExportDelivery();
  const isPaused = webhook.status === 'paused';
  const [openPayloadId, setOpenPayloadId] = React.useState<number | null>(null);

  const handleRedeliver = (delivery: DataExportDelivery) =>
    redeliver.mutate(
      { webhookId: webhook.id, deliveryId: delivery.id },
      {
        onSuccess: () => showToast({ status: 'success', message: t('dataExport.deliveries.redelivered') }),
        onError: e => showToast(errorToast(e, t('dataExport.deliveries.redeliverFailed'))),
      },
    );

  const renderOutcome = (delivery: DataExportDelivery) => {
    if (delivery.status === 'delivered' && delivery.delivered_at) {
      return t('dataExport.deliveries.deliveredAt', { time: formatExportDateTime(delivery.delivered_at) });
    }
    if (delivery.status === 'pending' && delivery.next_attempt_at) {
      return t('dataExport.deliveries.nextAttempt', { time: formatExportDateTime(delivery.next_attempt_at) });
    }
    return '—';
  };

  const renderTable = (deliveries: DataExportDelivery[]) => (
    <DeliveriesScroller>
      <DeliveriesTable data-testid='data-export-deliveries'>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th key={column}>
                <Text variant='body2' fontWeight='600'>
                  {t(`dataExport.deliveries.columns.${column}`)}
                </Text>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {deliveries.map(delivery => {
            const payloadId = `data-export-delivery-payload-${delivery.id}`;
            const isPayloadOpen = openPayloadId === delivery.id;
            return (
              <React.Fragment key={delivery.id}>
                <tr data-testid='data-export-delivery-row'>
                  <td>
                    <Text variant='body2'>{formatExportDateTime(delivery.created_at)}</Text>
                  </td>
                  <td>
                    <Code>{delivery.event}</Code>
                    {delivery.study_public_id && (
                      <Text variant='body2' color='black50'>
                        {delivery.export_sequence !== null
                          ? `${delivery.study_public_id} · #${delivery.export_sequence}`
                          : delivery.study_public_id}
                      </Text>
                    )}
                  </td>
                  <td>
                    <Code>{delivery.delivery_id}</Code>
                    <CopyButton value={delivery.delivery_id} />
                  </td>
                  <td>
                    <ToneBadge tone={STATUS_TONES[delivery.status]}>{t(`dataExport.deliveries.status.${delivery.status}`)}</ToneBadge>
                  </td>
                  <td>
                    <Text variant='body2'>{`${delivery.attempts} / ${delivery.max_attempts}`}</Text>
                  </td>
                  <td>
                    <Text variant='body2'>{delivery.last_response_code ?? '—'}</Text>
                    {delivery.last_error && (
                      <Text variant='body2' color='black50'>
                        {delivery.last_error}
                      </Text>
                    )}
                  </td>
                  <td>
                    <Text variant='body2'>{renderOutcome(delivery)}</Text>
                  </td>
                  <td>
                    <SecondaryButton
                      type='button'
                      color='white'
                      compact
                      isButtonV2
                      aria-expanded={isPayloadOpen}
                      aria-controls={payloadId}
                      onClick={() => setOpenPayloadId(isPayloadOpen ? null : delivery.id)}
                    >
                      {t(isPayloadOpen ? 'dataExport.deliveries.hidePayload' : 'dataExport.deliveries.showPayload')}
                    </SecondaryButton>
                  </td>
                  <td>
                    {delivery.status !== 'pending' && (
                      <SecondaryButton
                        type='button'
                        color='white'
                        compact
                        isButtonV2
                        disabled={isPaused || redeliver.isLoading}
                        title={isPaused ? t('dataExport.deliveries.pausedHint') : undefined}
                        onClick={() => handleRedeliver(delivery)}
                      >
                        {t('dataExport.deliveries.redeliver')}
                      </SecondaryButton>
                    )}
                  </td>
                </tr>
                {isPayloadOpen && (
                  <tr id={payloadId} data-testid='data-export-delivery-payload'>
                    <td colSpan={COLUMNS.length + 1}>
                      <Text variant='body2' color='black75'>
                        {t('dataExport.deliveries.payloadNote')}
                      </Text>
                      <CodeBlock code={formatPayload(delivery.payload)} copyValue={delivery.payload} language='json' />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </DeliveriesTable>
    </DeliveriesScroller>
  );

  const renderContent = () => {
    if (isLoading) return <SectionLoading />;
    if (isError) return <SectionError error={error} onRetry={() => refetch()} />;
    if (!data?.deliveries.length) {
      return (
        <Text variant='body2' color='black75'>
          {t('dataExport.deliveries.empty', { count: retentionDays ?? 30 })}
        </Text>
      );
    }
    return renderTable(data.deliveries);
  };

  return (
    <DeliveriesPanel id={`data-export-deliveries-${webhook.id}`} aria-label={t('dataExport.deliveries.title')}>
      <Text variant='body2' fontWeight='600'>
        {t('dataExport.deliveries.title')}
      </Text>
      {isPaused && (
        <Text variant='body2' color='wine'>
          {t('dataExport.deliveries.pausedHint')}
        </Text>
      )}
      {renderContent()}
    </DeliveriesPanel>
  );
};

export default WebhookDeliveries;
